import * as React from "react";
import { Search } from "lucide-react";
import { cn } from "../lib/utils";
import { Dialog, DialogContent, DialogTitle } from "./dialog";

/* Subsequence fuzzy matcher — higher score for consecutive and word-start hits. */
export function fuzzyScore(text: string, query: string): number | null {
  const t = text.toLowerCase();
  const q = query.toLowerCase().trim();
  if (!q) return 1;
  let score = 0;
  let cursor = 0;
  let streak = 0;
  for (const ch of q) {
    const idx = t.indexOf(ch, cursor);
    if (idx === -1) return null;
    streak = idx === cursor ? streak + 1 : 0;
    score += 1 + streak * 2;
    if (idx === 0 || t[idx - 1] === " " || t[idx - 1] === "/" || t[idx - 1] === "-") score += 3;
    cursor = idx + 1;
  }
  return score;
}

interface CommandItemRecord {
  value: string;
  keywords: string;
  disabled: boolean;
  el: HTMLElement | null;
  onSelect: ((value: string) => void) | undefined;
}

interface CommandCtx {
  search: string;
  setSearch: (v: string) => void;
  register: (rec: CommandItemRecord) => void;
  unregister: (value: string) => void;
  updateOnSelect: (value: string, fn: ((v: string) => void) | undefined) => void;
  visible: string[];
  activeValue: string | null;
  setActiveValue: (v: string | null) => void;
  select: (value: string) => void;
  shouldCloseOnSelect: boolean;
  setOpen: (open: boolean) => void;
  inputId: string;
}

const CommandContext = React.createContext<CommandCtx | null>(null);

function useCommandCtx(): CommandCtx {
  const ctx = React.useContext(CommandContext);
  if (!ctx) throw new Error("Command components must be used inside <Command>");
  return ctx;
}

export interface CommandProps {
  /** Controlled open state — Command renders a Dialog. */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Close the palette after selecting an item. Default true. */
  shouldCloseOnSelect?: boolean;
  /** Notify the host when the query changes (for async search). */
  onSearchChange?: (search: string) => void;
  className?: string;
  children?: React.ReactNode;
  "aria-label"?: string;
}

const Command = React.forwardRef<HTMLDivElement, CommandProps>(
  (
    {
      open,
      defaultOpen,
      onOpenChange,
      shouldCloseOnSelect = true,
      onSearchChange,
      className,
      children,
      ...aria
    },
    _outerRef,
  ) => {
    const [internalOpen, setInternalOpen] = React.useState(Boolean(defaultOpen));
    const isOpen = open ?? internalOpen;
    const setOpen = React.useCallback(
      (next: boolean) => {
        if (open === undefined) setInternalOpen(next);
        onOpenChange?.(next);
      },
      [open, onOpenChange],
    );

    const [search, setSearchState] = React.useState("");
    const [activeValue, setActiveValue] = React.useState<string | null>(null);
    const [version, setVersion] = React.useState(0);
    const itemsRef = React.useRef<Map<string, CommandItemRecord>>(new Map());
    const orderRef = React.useRef<string[]>([]);
    const inputId = React.useId();

    const setSearch = React.useCallback(
      (v: string) => {
        setSearchState(v);
        onSearchChange?.(v);
      },
      [onSearchChange],
    );

    const register = React.useCallback((rec: CommandItemRecord) => {
      if (!itemsRef.current.has(rec.value)) orderRef.current.push(rec.value);
      itemsRef.current.set(rec.value, rec);
      setVersion((v) => v + 1);
    }, []);

    const unregister = React.useCallback((value: string) => {
      itemsRef.current.delete(value);
      orderRef.current = orderRef.current.filter((v) => v !== value);
      setVersion((v) => v + 1);
    }, []);

    const updateOnSelect = React.useCallback(
      (value: string, fn: ((v: string) => void) | undefined) => {
        const rec = itemsRef.current.get(value);
        if (rec) rec.onSelect = fn;
      },
      [],
    );

    const visible = React.useMemo(() => {
      void version;
      return orderRef.current.filter((value) => {
        const rec = itemsRef.current.get(value);
        if (!rec || rec.disabled) return false;
        return fuzzyScore(`${rec.value} ${rec.keywords}`.trim(), search) !== null;
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [version, search]);

    const select = React.useCallback((value: string) => {
      const rec = itemsRef.current.get(value);
      if (!rec || rec.disabled) return;
      rec.onSelect?.(value);
      if (shouldCloseOnSelect) setOpen(false);
    }, [shouldCloseOnSelect, setOpen]);

    // Keep the active option inside the visible set.
    React.useEffect(() => {
      if (visible.length === 0) {
        if (activeValue !== null) setActiveValue(null);
      } else if (!activeValue || !visible.includes(activeValue)) {
        setActiveValue(visible[0] ?? null);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible.join("\u0000")]);

    // Scroll the active option into view.
    React.useEffect(() => {
      if (!activeValue) return;
      itemsRef.current.get(activeValue)?.el?.scrollIntoView({ block: "nearest" });
    }, [activeValue, version]);

    const ctx: CommandCtx = {
      search,
      setSearch,
      register,
      unregister,
      updateOnSelect,
      visible,
      activeValue,
      setActiveValue,
      select,
      shouldCloseOnSelect,
      setOpen,
      inputId,
    };

    const navigate = (key: React.KeyboardEvent["key"]) => {
      const list = visible;
      const idx = activeValue ? list.indexOf(activeValue) : -1;
      switch (key) {
        case "ArrowDown":
          setActiveValue(list[Math.min(list.length - 1, idx + 1)] ?? null);
          return true;
        case "ArrowUp":
          setActiveValue(list[Math.max(0, idx - 1)] ?? null);
          return true;
        case "Home":
          setActiveValue(list[0] ?? null);
          return true;
        case "End":
          setActiveValue(list[list.length - 1] ?? null);
          return true;
        case "Enter":
          if (activeValue) select(activeValue);
          return true;
        default:
          return false;
      }
    };

    return (
      <Dialog open={isOpen} onOpenChange={setOpen}>
        <DialogTitle className="sr-only">{aria["aria-label"] ?? "Command palette"}</DialogTitle>
        <DialogContent
          hideClose
          className={cn(
            "top-[10vh] translate-y-0 max-w-xl gap-0 overflow-hidden p-0",
            className,
          )}
        >
          <div
            className="flex flex-col outline-none"
            onKeyDown={(e) => {
              if (e.defaultPrevented) return;
              if (e.key === "Escape") {
                setOpen(false);
                return;
              }
              if (["ArrowDown", "ArrowUp", "Home", "End", "Enter"].includes(e.key)) {
                // Let Home/End fall through while editing text with a selection or modifiers.
                if ((e.key === "Home" || e.key === "End") && (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey)) return;
                if (navigate(e.key)) e.preventDefault();
              }
            }}
          >
            <CommandContext.Provider value={ctx}>{children}</CommandContext.Provider>
          </div>
        </DialogContent>
      </Dialog>
    );
  },
);
Command.displayName = "Command";

export interface CommandInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> {
  value?: string;
  onValueChange?: (value: string) => void;
}

const CommandInput = React.forwardRef<HTMLInputElement, CommandInputProps>(
  ({ className, placeholder = "Type a command or search…", value, onValueChange, ...props }, ref) => {
    const ctx = useCommandCtx();
    const innerRef = React.useRef<HTMLInputElement | null>(null);
    React.useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);

    return (
      <div className={cn("flex items-center gap-2 border-b border-border px-3", className)}>
        <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <input
          ref={innerRef}
          id={ctx.inputId}
          type="text"
          role="combobox"
          aria-expanded
          aria-controls="asrt-command-list"
          aria-autocomplete="list"
          autoComplete="off"
          spellCheck={false}
          placeholder={placeholder}
          value={value ?? ctx.search}
          onChange={(e) => {
            ctx.setSearch(e.target.value);
            onValueChange?.(e.target.value);
          }}
          className="h-11 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          {...props}
        />
      </div>
    );
  },
);
CommandInput.displayName = "CommandInput";

const CommandList = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        id="asrt-command-list"
        role="listbox"
        aria-label="Command results"
        tabIndex={-1}
        className={cn("max-h-80 scroll-p-1 overflow-y-auto overflow-x-hidden p-1", className)}
        {...props}
      />
    );
  },
);
CommandList.displayName = "CommandList";

const CommandEmpty = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const ctx = useCommandCtx();
    if (ctx.visible.length > 0) return null;
    return (
      <div
        ref={ref}
        role="status"
        className={cn("py-8 text-center text-sm text-muted-foreground", className)}
        {...props}
      />
    );
  },
);
CommandEmpty.displayName = "CommandEmpty";

export interface CommandGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  heading?: string;
}

const CommandGroup = React.forwardRef<HTMLDivElement, CommandGroupProps>(
  ({ className, heading, children, ...props }, ref) => {
    const headingId = heading ? `${React.useId()}-heading` : undefined;
    return (
      <div
        ref={ref}
        role="group"
        aria-labelledby={headingId}
        className={cn("p-1 text-foreground", className)}
        {...props}
      >
        {heading ? (
          <div id={headingId} className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {heading}
          </div>
        ) : null}
        {children}
      </div>
    );
  },
);
CommandGroup.displayName = "CommandGroup";

const CommandSeparator = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} role="separator" className={cn("-mx-1 h-px bg-border", className)} {...props} />
  ),
);
CommandSeparator.displayName = "CommandSeparator";

export interface CommandItemProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onSelect"> {
  /** Value used for fuzzy filtering + keyboard selection. */
  value: string;
  /** Extra keywords to match against (e.g. "deploy ship push"). */
  keywords?: string;
  disabled?: boolean;
  onSelect?: (value: string) => void;
}

const CommandItem = React.forwardRef<HTMLDivElement, CommandItemProps>(
  ({ className, value, keywords = "", disabled = false, onSelect, onMouseEnter, ...props }, ref) => {
    const ctx = useCommandCtx();
    const innerRef = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
      if (typeof ref === "function") ref(innerRef.current);
      else if (ref) ref.current = innerRef.current;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    });

    // Stable registration keyed on identity props only; onSelect refreshes
    // through updateOnSelect so inline callbacks can't loop the registry.
    React.useEffect(() => {
      ctx.register({ value, keywords, disabled, el: innerRef.current, onSelect: undefined });
      return () => ctx.unregister(value);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, keywords, disabled]);
    ctx.updateOnSelect(value, onSelect);

    const active = ctx.activeValue === value;

    return (
      <div
        ref={innerRef}
        role="option"
        aria-selected={active}
        aria-disabled={disabled || undefined}
        data-value={value}
        tabIndex={-1}
        onMouseEnter={(e) => {
          onMouseEnter?.(e);
          if (!disabled) ctx.setActiveValue(value);
        }}
        onClick={() => {
          if (!disabled) ctx.select(value);
        }}
        className={cn(
          "relative flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none [&_svg]:size-4 [&_svg]:shrink-0",
          active && "bg-secondary text-secondary-foreground",
          disabled && "pointer-events-none opacity-50",
          className,
        )}
        {...props}
      />
    );
  },
);
CommandItem.displayName = "CommandItem";

export { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator };
